/**
 * `PictogramService` — `/api/v1/events/{eventId}/pictograms/*` и
 * `/api/v1/events/{eventId}/pictogram-icons/*`.
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** Пиктограмма — картинка на бейдже, говорящая,
 * что человеку положено: обед, автобус, парковочное место. Она не описывает
 * ничего из того, что человек может сделать в приложении: права по-прежнему
 * описывают `auth.isAdmin` и `event.canManage`.
 *
 * ⚠️ Четвёртый справочник, а не замена трём предыдущим. Зона — «куда пускают»,
 * уровень — «насколько глубоко», пиктограмма — «что положено». У роли бывают
 * все три набора сразу и ни одного.
 *
 * Справочник **локален** для мероприятия.
 *
 * ПРАВА те же, что и у зон: admin и owner наполняют, viewer читает (включая
 * байты иконок), не участник получает 404 `EVENT_NOT_FOUND`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ИКОНКА — ДВА ШАГА, А НЕ ОДИН
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `uploadIcon` кладёт байты и отдаёт КЛЮЧ; `create`/`update` берут этот ключ
 * строкой. Так и должно быть: человек загружает картинку, смотрит на неё и
 * может выбрать другую, и связать эти два шага в один запрос значило бы, что
 * неудавшаяся запись оставляет запись, указывающую в никуда.
 */

import { http } from '@/lib/http'
import { ApiError, normalizeBlobError } from '@/lib/api-error'
import type {
  CreatePictogramRequest,
  CreatePictogramResponse,
  DeletePictogramResponse,
  ListPictogramsResponse,
  PictogramIcon,
  UpdatePictogramRequest,
  UpdatePictogramResponse,
} from '@/types/api'

function path(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/pictograms${suffix}`
}

function iconsPath(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/pictogram-icons${suffix}`
}

export const pictogramsApi = {
  /**
   * `GET .../pictograms` — весь справочник, **в порядке сортировки**. Читает
   * любой участник мероприятия.
   *
   * ⚠️ Порядок задаёт сервер: `(sortOrder, name)`. Пересортировывать на
   * клиенте нельзя — `sortOrder` НЕ уникален, и сортировка только по нему
   * перетасовывала бы записи с одинаковой позицией на каждой перерисовке, под
   * курсором у человека, который заполняет форму.
   */
  async list(eventId: string, signal?: AbortSignal): Promise<ListPictogramsResponse> {
    const { data } = await http.get<ListPictogramsResponse>(path(eventId), { signal })
    return data
  },

  /**
   * `POST .../pictograms` — admin или owner.
   *
   * `409 PICTOGRAM_ALREADY_EXISTS` с `metadata.name` — название занято (без
   * учёта регистра). Ошибку вешают на поле НАЗВАНИЯ.
   *
   * ⚠️ `sortOrder` обязан быть числом. Из `<input type="number">` значение
   * приходит строкой, и `"10"` в теле вернётся `VALIDATION_FAILED` с
   * `metadata.field = "sortOrder"`.
   */
  async create(eventId: string, body: CreatePictogramRequest): Promise<CreatePictogramResponse> {
    const { data } = await http.post<CreatePictogramResponse>(path(eventId), body)
    return data
  },

  /**
   * `PUT .../pictograms/{id}` — все три поля разом.
   *
   * ⚠️ Пустой `iconKey` здесь СТИРАЕТ иконку, а не означает «не менять» — иначе
   * убрать картинку было бы нечем. Сервер при этом удаляет и файл: ключ хранится
   * ровно в этой строке, и после правки назвать объект больше нечем.
   */
  async update(
    eventId: string,
    id: string,
    body: UpdatePictogramRequest,
  ): Promise<UpdatePictogramResponse> {
    const { data } = await http.put<UpdatePictogramResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
      body,
    )
    return data
  },

  /**
   * `DELETE .../pictograms/{id}` — жёсткое удаление.
   *
   * ⚠️ Привязки к ролям уходят каскадом НА СЕРВЕРЕ: у каждой роли, которая
   * выдавала эту пиктограмму, она молча пропадёт. Клиент об этом не узнает сам —
   * стор ролей продолжит держать id несуществующей записи, пока его не
   * перечитают. Поэтому вызывающий обязан (а) назвать эти роли в подтверждении
   * и (б) после удаления перечитать справочник ролей.
   *
   * ⚠️ Файл иконки удаляется вместе с записью, и это НЕ то же, что у макета
   * бейджа: там картинки переживают удаление шаблона, потому что человек
   * продолжает работать над тем же мероприятием. Здесь ключ лежал ровно в
   * удалённой строке, и назвать этот объект больше нечем.
   */
  async remove(eventId: string, id: string): Promise<DeletePictogramResponse> {
    const { data } = await http.delete<DeletePictogramResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
    )
    return data
  },

  /**
   * `POST .../pictogram-icons` — загрузить иконку. admin или owner.
   *
   * multipart с полем `file`, ровно как у картинок бейджа.
   *
   * ⚠️ `Content-Type` НЕ выставляется руками: его обязан собрать браузер вместе
   * с `boundary`, а перехватчик в `lib/http.ts` для `FormData` в тело не лезет.
   * Написать `'multipart/form-data'` самому — значит отправить запрос без
   * boundary, который сервер не разберёт.
   *
   * Сервер пересобирает картинку сам и сам решает формат по пикселям (PNG с
   * прозрачностью остаётся PNG, без неё становится JPEG), поэтому ответ несёт
   * итоговый `contentType` и пиксельные габариты — по ним форма рисует превью в
   * пропорциях картинки.
   *
   * ⚠️ Ответ — это ЕЩЁ НЕ ЗАПИСЬ. Ключ надо отправить в `create`/`update`;
   * загрузка, за которой не последовало записи, оставляет в хранилище объект,
   * на который никто не ссылается.
   */
  async uploadIcon(eventId: string, file: File, signal?: AbortSignal): Promise<PictogramIcon> {
    const form = new FormData()
    // Имя файла сервер не читает вовсе (расширение он ставит сам, по тому, что
    // сам же собрал), но FormData без имени шлёт поле как строку, а не как
    // файл, — и `FormFile` на той стороне его не находит.
    form.append('file', file, file.name || 'pictogram-icon')

    const { data } = await http.post<PictogramIcon>(iconsPath(eventId), form, { signal })
    return data
  },

  /**
   * `GET .../pictogram-icons/{name}` — сами байты.
   *
   * ⚠️ Эндпоинт требует `Authorization: Bearer`, поэтому навести на него
   * `<img src>` нельзя: картинка молча превратилась бы в «битое изображение», в
   * котором лежит 401. Байты забираются axios'ом (с обновлением токена, единой
   * обработкой ошибок и отменой) — ровно как флаг страны и подложка бейджа.
   *
   * Принимает ПОЛНЫЙ ключ из записи и берёт из него только имя файла:
   * мероприятие в пути запроса задаёт вызывающий, и оно авторитетнее того, что
   * записано в строке. Ключ чужого мероприятия сервер и так отвергает на
   * записи — здесь это вторая линия.
   */
  async fetchIcon(eventId: string, keyOrName: string, signal?: AbortSignal): Promise<Blob> {
    const name = keyOrName.slice(keyOrName.lastIndexOf('/') + 1)
    if (!name) {
      throw new ApiError(404, 'PICTOGRAM_ICON_NOT_FOUND')
    }

    try {
      const { data } = await http.get<Blob>(iconsPath(eventId, `/${encodeURIComponent(name)}`), {
        responseType: 'blob',
        signal,
      })
      return data
    } catch (err) {
      // ⚠️ `responseType: 'blob'` означает, что и ТЕЛО ОШИБКИ приезжает блобом:
      // перехватчик в `lib/http.ts` синхронный и разобрать его не может, а без
      // разбора «нет иконки» превратилось бы в безымянный 404 без причины.
      throw await normalizeBlobError(err)
    }
  },
}
